import { useLayoutEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { FluentProvider, webLightTheme, Spinner } from '@fluentui/react-components';
import { AppShell } from 'components/AppShell';
import { ProtectedRoute } from 'components/Auth';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { StatusPanelPage } from 'pages/StatusPanel';
import { LoginPage } from 'pages/Login';
import { UnauthorizedPage } from 'pages/Unauthorized';
import { AdminConsentRedirectPage } from 'pages/AdminConsentRedirect';
import { StatusPanelProvider, useStatusPanelMode, ToastProvider, AuthProvider, useAuthContext, SignalRProvider, SecurityGroupConfigProvider } from 'contexts';
import { NotificationManager } from 'components/NotificationManager';
import { SettingsSecuritySync } from 'components/SettingsSecuritySync';
import { wallScreenDarkTheme } from 'themes';
import { setAuthTokenProvider } from 'services';

// Lazy-loaded pages (code splitting)
const LogManagementPage = lazy(() => import('pages/LogManagement').then(m => ({ default: m.LogManagementPage })));
const OnboardingPage = lazy(() => import('pages/Onboarding').then(m => ({ default: m.OnboardingPage })));

// Loading fallback for lazy components
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spinner size="large" label="Loading..." />
  </div>
);

/**
 * Initializes API with auth token provider
 * Uses useLayoutEffect to ensure token provider is set before other effects run
 * Note: SignalR token provider is set by SignalRProvider
 */
function AuthTokenInitializer() {
  const { getAccessToken } = useAuthContext();

  useLayoutEffect(() => {
    // Wire up token provider for API synchronously before other effects
    setAuthTokenProvider(getAccessToken);
  }, [getAccessToken]);

  return null;
}

function ThemedRoutes() {
  const { isWallScreen } = useStatusPanelMode();
  const theme = isWallScreen ? wallScreenDarkTheme : webLightTheme;

  return (
    <FluentProvider theme={theme}>
      <ToastProvider>
        <AuthTokenInitializer />
        <SettingsSecuritySync />
        <Routes>
          {/* Public routes */}
          <Route path="login" element={<LoginPage />} />
          <Route path="unauthorized" element={<UnauthorizedPage />} />
          <Route path="admin-consent-redirect" element={<AdminConsentRedirectPage />} />

          {/* Protected routes */}
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/status-panel" replace />} />
            <Route
              path="status-panel"
              element={
                <ProtectedRoute allowPublic>
                  <StatusPanelPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="log-management"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <LogManagementPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="onboarding"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <OnboardingPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </ToastProvider>
    </FluentProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <SecurityGroupConfigProvider>
          <AuthProvider>
            <StatusPanelProvider>
              <SignalRProvider>
                <NotificationManager>
                  <ThemedRoutes />
                </NotificationManager>
              </SignalRProvider>
            </StatusPanelProvider>
          </AuthProvider>
        </SecurityGroupConfigProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
